/**
 * EnergySystem — tracks and manages an energy resource for a character.
 *
 * Used by:
 *   - Basil: "Kinetic Battery" — drains on heavy abilities, recharges passively
 *   - Minna: "Ethereal Focus" — drains near threats and while using perception abilities
 *
 * The system is opt-in per level: levels that don't create EnergySystem instances
 * are unaffected (Levels 0-1 can skip it entirely).
 *
 * Communicates state changes via the typed EventBus so HUD and abilities react
 * without direct coupling.
 */

import { gameEvents } from '../core/EventBus';
import { GameEvents } from '../config/events';

export interface EnergyConfig {
  /** Maximum energy capacity. */
  maxEnergy: number;
  /** Passive recharge rate in energy units per second. */
  rechargeRate: number;
  /** Below this threshold, depleted penalties kick in. */
  depletedThreshold: number;
  /** Character identifier emitted with events ("minna" or "basil"). */
  character: string;
}

export class EnergySystem {
  current: number;
  max: number;
  config: EnergyConfig;
  isDepleted: boolean = false;

  constructor(config: EnergyConfig) {
    this.config = config;
    this.max = config.maxEnergy;
    this.current = config.maxEnergy;
  }

  /**
   * Drain energy by the given amount.
   * Returns false if there is insufficient energy (no drain occurs).
   */
  drain(amount: number): boolean {
    if (this.current < amount) return false;
    this.current = Math.max(0, this.current - amount);
    this.emitChanged();
    this.checkDepletedTransition();
    return true;
  }

  /**
   * Recharge energy by the given amount (capped at max).
   */
  recharge(amount: number): void {
    if (amount <= 0) return;
    const prev = this.current;
    this.current = Math.min(this.max, this.current + amount);
    if (this.current !== prev) {
      this.emitChanged();
      this.checkRestoredTransition();
    }
  }

  /**
   * Per-frame update: applies passive recharge and checks depleted state.
   * @param dt Delta time in seconds.
   */
  update(dt: number): void {
    // Passive recharge
    if (this.current < this.max && this.config.rechargeRate > 0) {
      this.recharge(this.config.rechargeRate * dt);
    }
  }

  /** Current energy as a 0-1 fraction. */
  get fraction(): number {
    return this.max > 0 ? this.current / this.max : 0;
  }

  // ── Internal ──────────────────────────────────────────────────────

  private emitChanged(): void {
    gameEvents.emit(GameEvents.ENERGY_CHANGED, {
      character: this.config.character,
      current: this.current,
      max: this.max,
    });
  }

  private checkDepletedTransition(): void {
    if (!this.isDepleted && this.current <= this.config.depletedThreshold) {
      this.isDepleted = true;
      gameEvents.emit(GameEvents.ENERGY_DEPLETED, { character: this.config.character });
    }
  }

  private checkRestoredTransition(): void {
    if (this.isDepleted && this.current > this.config.depletedThreshold) {
      this.isDepleted = false;
      gameEvents.emit(GameEvents.ENERGY_RESTORED, { character: this.config.character });
    }
  }
}
